import React, { useCallback, useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";

// local component imports
import MediaCell from "./MediaCell";
import CellCaption from "../CellCaption";
import { shouldPrioritizeCarouselCell } from "../../../../../utils/dynamicHomeUtils/lcpWidget";

/**
 * FreeFlowCarousel renders a native horizontally-scrollable carousel for mobile.
 * Supports single-row and multi-row (stacked columns) layouts.
 *
 * @param {object} props
 * @param {Array}  props.validCells    - Flat array of cell data objects sorted by sortOrder
 * @param {number} props.slidesToShow  - Number of columns visible at once (e.g. 2.3)
 * @param {number} props.rowCount      - Number of rows per column group (default 1)
 * @param {number} props.slideGap      - Horizontal gap between columns in px
 * @param {number} props.rowGap        - Vertical gap between rows in px
 * @param {number} props.aspectRatio   - Aspect ratio for each cell
 * @param {number} props.borderRadius  - Border radius in px for each cell
 * @param {object} props.gridTextStyle - Text style config from sectionSettings
 * @param {object} props.sectionPadding - Padding config from sectionSettings
 * @param {boolean} props.isAboveFold - Whether this carousel is expected above the fold
 * @param {boolean} props.isLcpCandidate - Whether this carousel may contain the LCP image
 * @returns {React.ReactElement}
 */
const FreeFlowCarousel = ({
  validCells,
  slidesToShow,
  rowCount = 1,
  slideGap = 15,
  rowGap = 12,
  aspectRatio = 1,
  borderRadius = 0,
  gridTextStyle = {},
  sectionPadding,
  borderThickness,
  borderColor,
  isShowSlider = false,
  isMobile,
  isAuto,
  isAboveFold,
  isLcpCandidate,
}) => {
  const [playingVideos, setPlayingVideos] = useState(new Set());
  const [loadedVideos, setLoadedVideos] = useState(new Set());
  const videoRefs = useRef(new Map());
  const scrollRef = useRef(null);
  const [scrollProgress, setScrollProgress] = useState(0);

  const {
    textEnabled = false,
    innerText = false,
    fontSize: textFontSize,
    fontWeight: textFontWeight,
    fontFamily: textFontFamily,
    titleColor: textColor,
    horizontalAlignment = "center",
    verticalAlignment = "bottom",
  } = gridTextStyle || {};

  const textStyle = {
    fontSize: textFontSize ? `${textFontSize}px` : undefined,
    fontWeight: textFontWeight || undefined,
    fontFamily: textFontFamily || undefined,
    color: textColor || undefined,
    textAlign: horizontalAlignment,
  };

  const verticalPositionClass =
    verticalAlignment === "top" ? "top-0" : verticalAlignment === "center" ? "top-1/2 -translate-y-1/2" : "bottom-0";
  const overlayTopPaddingClass = verticalAlignment === "top" ? "pt-[8px] md:pt-[12px]" : "";

  /**
   * Toggles play/pause for the video at the given cell index.
   */
  const onToggleVideoPlay = useCallback(
    (index) => {
      const video = videoRefs.current.get(index);
      if (!video) return;
      // playingVideos is driven by the element's own play/pause events, so only drive the element here.
      if (playingVideos.has(index)) video.pause();
      else video.play()?.catch(() => {});
    },
    [playingVideos],
  );

  /**
   * Tracks scroll position and updates progress.
   */
  const onScrollProgress = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const { scrollLeft, scrollWidth, clientWidth } = el;
    const maxScroll = scrollWidth - clientWidth;
    setScrollProgress(maxScroll > 0 ? (scrollLeft / maxScroll) * 100 : 0);
  }, []);

  useEffect(() => {
    if (!isAuto || !isMobile) return;
    const el = scrollRef.current;
    if (!el) return;

    let isPaused = false;
    let cancelScroll = null;

    /** @param {number} t @returns {number} */
    const easeInOutCubic = (t) => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2);

    // rAF-based smooth scroll — avoids browser's jerky native smooth behavior
    /** @param {number} distance @param {number} duration @returns {Function} cancel */
    const smoothScrollBy = (distance, duration) => {
      const start = el.scrollLeft;
      const startTime = performance.now();
      let rafId;
      /** @param {number} now @returns {void} */
      const step = (now) => {
        const progress = Math.min((now - startTime) / duration, 1);
        el.scrollLeft = start + distance * easeInOutCubic(progress);
        if (progress < 1) rafId = requestAnimationFrame(step);
      };
      rafId = requestAnimationFrame(step);
      return () => cancelAnimationFrame(rafId);
    };

    /** @returns {void} */
    const advance = () => {
      if (isPaused) return;
      if (cancelScroll) cancelScroll();
      const { scrollLeft, scrollWidth, clientWidth } = el;
      if (scrollLeft >= scrollWidth - clientWidth - 1) {
        return; // reached the end — stop auto-scrolling
      } else {
        const colWidth = el.firstElementChild?.offsetWidth ?? 0;
        cancelScroll = smoothScrollBy(colWidth + slideGap, 600);
      }
    };

    /** @returns {void} */
    const pause = () => {
      isPaused = true;
    };
    /** @returns {void} */
    const resume = () => {
      isPaused = false;
    };

    el.addEventListener("touchstart", pause, { passive: true });
    el.addEventListener("touchend", resume, { passive: true });

    const id = setInterval(advance, 2000);

    return () => {
      clearInterval(id);
      if (cancelScroll) cancelScroll();
      el.removeEventListener("touchstart", pause);
      el.removeEventListener("touchend", resume);
    };
  }, [isAuto, isMobile, slideGap]);

  // Width per column as a CSS calc string so partial slides peek through
  const slideWidth = `calc(${100 / slidesToShow}% - ${(slideGap * (slidesToShow - 1)) / slidesToShow}px)`;

  // Group flat cells into column stacks of `rowCount` items each.
  // e.g. 6 cells, rowCount=2 → [[cell0, cell1], [cell2, cell3], [cell4, cell5]]
  // But slick rows=2 groups by: numCols = total/rowCount, col j = [cells[j], cells[j+numCols]]
  // so row1 items come first, then row2 items at offset numCols.
  const numCols = rowCount > 1 ? Math.ceil(validCells.length / rowCount) : validCells.length;
  const columns = Array.from({ length: numCols }, (_, colIdx) =>
    Array.from({ length: rowCount }, (__, rowIdx) => {
      const cellIdx = rowIdx * numCols + colIdx;
      return validCells[cellIdx] ?? null;
    }).filter(Boolean),
  );

  return (
    <div>
      <div
        ref={scrollRef}
        className="scrollbar-hide flex w-full overflow-x-auto"
        style={{
          gap: `${slideGap}px`,
          paddingLeft: sectionPadding?.paddingLeft,
          paddingRight: sectionPadding?.paddingRight,
        }}
        role="region"
        aria-label="Product carousel"
        data-component="FreeFlowCarousel"
        onScroll={onScrollProgress}
      >
        {columns.map((columnCells, colIdx) => (
          <div key={`col-${colIdx}`} className="relative flex-shrink-0" style={{ width: slideWidth }}>
            {/* Stacked rows inside the column — each cell owns its own text */}
            <div className="flex flex-col" style={{ gap: `${rowGap}px` }}>
              {columnCells.map((cell, rowIdx) => {
                const globalIdx = rowIdx * numCols + colIdx;
                const cellText = cell?.textContent || cell?.title;
                return (
                  <div key={`cell-${globalIdx}`}>
                    <MediaCell
                      cell={cell}
                      index={globalIdx}
                      borderRadius={borderRadius}
                      aspectRatio={aspectRatio}
                      loadedVideos={loadedVideos}
                      videoRefs={videoRefs}
                      onToggleVideoPlay={onToggleVideoPlay}
                      setLoadedVideos={setLoadedVideos}
                      setPlayingVideos={setPlayingVideos}
                      borderThickness={borderThickness}
                      borderColor={borderColor}
                      isEager={shouldPrioritizeCarouselCell(globalIdx, isAboveFold)}
                      isPriority={shouldPrioritizeCarouselCell(globalIdx, isLcpCandidate)}
                    />
                    {textEnabled && cellText && !innerText && (
                      <CellCaption style={textStyle} wrapperClassName="pt-[6px]">
                        {cellText}
                      </CellCaption>
                    )}
                    {textEnabled && cellText && innerText && (
                      <div
                        className={`absolute left-0 right-0 px-[${isMobile ? "8" : "15"}px] pb-[${isMobile ? "8" : "15"}px] ${verticalPositionClass} ${overlayTopPaddingClass}`}
                      >
                        <p className="break-words" style={textStyle}>
                          {cellText}
                        </p>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
      {/* marking it false as instructed by the designer, keeping the code, they might change their mind later */}
      {isShowSlider && false && (
        <div className="mx-auto mt-[12px] w-full max-w-[60vw]">
          <div
            className="block h-[4px] w-full overflow-hidden rounded bg-[#E0E0E0]"
            role="progressbar"
            aria-valuemin="0"
            aria-valuemax="100"
            aria-valuenow={scrollProgress}
            style={{
              backgroundImage: "linear-gradient(to right, #737530, #737530)",
              backgroundRepeat: "no-repeat",
              backgroundSize: `${scrollProgress}% 100%`,
              transition: "background-size 0.2s ease",
            }}
          >
            <span className="sr-only">{Math.round(scrollProgress)}% scrolled</span>
          </div>
        </div>
      )}
    </div>
  );
};

FreeFlowCarousel.propTypes = {
  validCells: PropTypes.arrayOf(PropTypes.object).isRequired,
  slidesToShow: PropTypes.number.isRequired,
  rowCount: PropTypes.number,
  slideGap: PropTypes.number,
  rowGap: PropTypes.number,
  aspectRatio: PropTypes.number,
  borderRadius: PropTypes.number,
  gridTextStyle: PropTypes.object,
  sectionPadding: PropTypes.object,
  borderThickness: PropTypes.number,
  borderColor: PropTypes.string,
  isShowSlider: PropTypes.bool,
  isMobile: PropTypes.bool.isRequired,
  isAuto: PropTypes.bool,
  isAboveFold: PropTypes.bool,
  isLcpCandidate: PropTypes.bool,
};

export default FreeFlowCarousel;
